import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { AgentsService } from '../services/agents.service';
import { UploadService } from '../components/expense/services/upload.service';
import { FilesService } from '../services/files.service';
import { HostService } from '../services/host.service';

@Injectable({
  providedIn: 'root'
})
export class UploadHandlerService {
  constructor(
    private agentService: AgentsService,
    private uploadService: UploadService,
    private filesService: FilesService,
    private hostService: HostService
  ) { }

  
  async handleFileUpload(
    event: Event,
    propertyId: number | string,
    schemaName: string,
    onComplete: () => void,
    onError: (err: any) => void
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
  
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
  
    try {
      if (['jpg', 'jpeg', 'png'].includes(fileExtension)) {
        await this.handleDocumentUpload(file, propertyId, schemaName, 'image');
      } else if (['xlsx', 'csv'].includes(fileExtension)) {
        await this.handleSpreadsheetUpload(file, propertyId, schemaName);
      } else {
        await this.handleDocumentUpload(file, propertyId, schemaName);
      }
  
      onComplete();
    } catch (err) {
      console.error("Upload Error:", err);
      onError(err);
    }
  }
  
  private async handleDocumentUpload(
    file: File,
    propertyId: number | string,
    schemaName: string,
    type: 'image' | 'pdf' = 'pdf'
  ): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
  
    try {
      const uploadRes = await lastValueFrom(this.agentService.sendFile(formData));
  
      const payload = {
        schemaType: schemaName,
        file_source: uploadRes.filename,
        subjectPropertyId: propertyId,
        file_id: uploadRes.id,
      };
  
      const uploadMethod = type === 'image'
        ? this.agentService.sendImageFile(payload)
        : this.agentService.sendPdfFile(payload);
  
      await lastValueFrom(uploadMethod);
    } catch (error) {
      console.error('Document upload failed:', error);
    }
  }
  
  private async handleSpreadsheetUpload(file: File, propertyId: number | string, schemaName: string): Promise<void> {
    const uploadRes = await lastValueFrom(this.uploadService.getUploadUrl(1, "chat-uploads"));

    const uploadUrl = uploadRes.success.data[0]?.urls[0];
    if (!uploadUrl) throw new Error("Upload URL is invalid");

    await lastValueFrom(this.uploadService.uploadFile(file, uploadUrl));

    const sanitizedUrl = uploadUrl.split('?')[0];
    const fileMeta = {
      url: sanitizedUrl,
      type: file.type,
      name: file.name,
    };

    const response = await lastValueFrom(this.filesService.uploadFile(fileMeta));
    const uploadedDetails = response.data.uploadFile;
    const tenant = this.hostService.getCurrentTenant();

    fileMeta.url = `${environment.baseUrl}/api/host/files/${uploadedDetails.id}/${tenant.tenancyName}/${file.name}`;

    const payload = {
      schemaType: schemaName,
      file_source: 'costar',
      subjectPropertyId: propertyId,
      file_url: fileMeta.url,
    };

    await lastValueFrom(this.agentService.sendCsvFile(payload));
  }


}